/**
 * ============================================================================
 * 📅 DATE PICKER MODAL - Modale Selezione Data per Duplicazione
 * ============================================================================
 * 
 * Componente modale per selezionare la data del rapportino duplicato
 * Prima di duplicare, l'utente sceglie la data target
 * 
 * @version 1.0.0
 * @file src/components/shared/DatePickerModal.jsx
 */

import React, { useState, useEffect } from 'react';
import './DatePickerModal.css';

const DatePickerModal = ({ 
  isOpen, 
  onClose, 
  onConfirm,
  originalDate,
  title = "Seleziona Data Rapportino"
}) => {
  // Imposta data di default a oggi
  const [selectedDate, setSelectedDate] = useState('');
  
  useEffect(() => {
    if (isOpen) {
      // Imposta data di oggi come default quando si apre il modal
      const today = new Date().toISOString().split('T')[0];
      setSelectedDate(today);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!selectedDate) {
      alert('⚠️ Seleziona una data');
      return;
    }
  
    // 🆕 CONFRONTA STRINGHE INVECE DI DATE OBJECT
    const todayStr = new Date().toISOString().split('T')[0];
    
    if (selectedDate > todayStr) {
      alert('⚠️ Non puoi selezionare una data futura');
      return;
    }
  
    onConfirm(selectedDate);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Formatta la data originale per il display
  const formatDisplayDate = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('it-IT', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Suggerimenti date comuni
  const getQuickDates = () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const twoDaysAgo = new Date(today);
    twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);

    return [
      { label: 'Oggi', date: today.toISOString().split('T')[0] },
      { label: 'Ieri', date: yesterday.toISOString().split('T')[0] },
      { label: '2 giorni fa', date: twoDaysAgo.toISOString().split('T')[0] }
    ];
  };

  const quickDates = getQuickDates();

  return (
    <div className="date-picker-overlay" onClick={handleOverlayClick}>
      <div className="date-picker-modal">
        {/* Header */}
        <div className="date-picker-header">
          <h3>{title}</h3>
          <button 
            className="date-picker-close"
            onClick={onClose}
            title="Chiudi"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="date-picker-content">
          {/* Info rapportino originale */}
          {originalDate && (
            <div className="original-date-info">
              <span className="info-icon">ℹ️</span>
              <div>
                <strong>Rapportino originale:</strong>
                <br />
                {formatDisplayDate(originalDate)}
              </div>
            </div>
          )}

          {/* Selezione data */}
          <div className="date-selection-section">
            <label htmlFor="target-date">
              <strong>Seleziona la data del nuovo rapportino:</strong>
            </label>
            <input
              id="target-date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]} // Non permettere date future
              className="date-picker-input"
              autoFocus
            />
          </div>

          {/* Quick date buttons */}
          <div className="quick-dates-section">
            <label>Oppure scegli rapidamente:</label>
            <div className="quick-dates-buttons">
              {quickDates.map((quick) => (
                <button
                  key={quick.label}
                  onClick={() => setSelectedDate(quick.date)}
                  className={`quick-date-btn ${selectedDate === quick.date ? 'selected' : ''}`}
                >
                  {quick.label}
                </button>
              ))}
            </div>
          </div>

          {/* Preview data selezionata */}
          {selectedDate && (
            <div className="selected-date-preview">
              <span className="preview-icon">📅</span>
              <div>
                <strong>Data selezionata:</strong>
                <br />
                {formatDisplayDate(selectedDate)}
              </div>
            </div>
          )}

          {/* Info sincronizzazione */}
          <div className="sync-info-box">
            <span className="sync-icon">🔄</span>
            <p>
              Le ore del personale verranno sincronizzate automaticamente 
              da <strong>workHours</strong> per la data selezionata.
            </p>
          </div>
        </div>

        {/* Footer con azioni */}
        <div className="date-picker-footer">
          <button 
            className="btn-cancel"
            onClick={onClose}
          >
            Annulla
          </button>
          <button 
            className="btn-confirm"
            onClick={handleConfirm}
            disabled={!selectedDate}
          >
            ✓ Duplica Rapportino
          </button>
        </div>
      </div>
    </div>
  );
};

export default DatePickerModal;